import { isNova } from '@/config'
import type { Comment } from '@/hooks/queries/useComments'
import type { CoupledOrderDetails, LegDetails, OrderJourneyStep } from '@repo/backend/nova'
import { isDeliveredCode, NOVA_JOURNEY_STEPS, type NovaJourneyStep } from '@repo/constants'
import type { LegJourney } from '../../routes/_auth/_modules/_app/orders/-components/NovaOrderDetails/OrderJourney'

/**
 * Helpers for the Nova order-details tree, kept out of the shared `orders.ts` because every one of
 * them takes a coupled response: a shape only the Nova line produces.
 */
export type CoupledOrder = NonNullable<CoupledOrderDetails>

/**
 * The payment term in force: the reviewed term when Fuze Core has written one, otherwise the term
 * the order arrived with. The counterpart to `getEffectiveDeliveryDate` below, which reads
 * `mod_rdd_date` the same way.
 *
 * Takes the two values rather than a row: the order list and the order-details leg carry them on
 * different shapes, and both need this.
 */
export function getEffectivePaymentTerm(
  paymentTerms: string | null | undefined,
  modPaymentTerm: string | null | undefined,
): string | null {
  return modPaymentTerm ?? paymentTerms ?? null
}

/**
 * Whether a reviewer actually moved the term, as opposed to Fuze Core merely having written one.
 *
 * The distinction is real in the data: a large share of the orders carrying a `mod_payment_term`
 * carry the SAME value as `payment_terms`. Testing for presence alone would caption those with an
 * "original" that is identical to the current term, which is noise on every one of them.
 */
export function hasModifiedPaymentTerm(
  paymentTerms: string | null | undefined,
  modPaymentTerm: string | null | undefined,
): boolean {
  return !!modPaymentTerm && modPaymentTerm !== paymentTerms
}

/**
 * The injection file a leg arrived in, while the leg is still under payment term review. Null
 * otherwise: once an order has been released or cancelled the reviewer is done with it.
 *
 * Per leg, not per order. The two legs of a coupled order can come from different files, since the
 * FMC and NC halves are often injected separately and coupled afterwards.
 */
export function getReviewInjInitiative(
  leg: Pick<LegDetails, 'injInitiativeName' | 'ptrStatus'>,
): string | null {
  return leg.ptrStatus === 'UNDER REVIEW' ? (leg.injInitiativeName ?? null) : null
}

export function getEffectiveDeliveryDate(
  leg: Pick<LegDetails, 'reqDelDate' | 'modRddDate'>,
): string | null {
  return leg.modRddDate ?? leg.reqDelDate
}

/**
 * A leg's quantities may only be changed while it is flagged for review and still held. Once
 * released the quantities are with SAP and no longer ours to edit.
 */
export function isLegQuantityEditable(leg: LegDetails | null | undefined): boolean {
  return !!leg?.flagged && leg.status === 'HOLD'
}

/**
 * Coupled legs only ship together if they carry the same requested delivery date. Reported to the
 * reviewer, but it never blocks release — a requested date is a request, not a guarantee. Only
 * meaningful while the order is still coupled: once decoupled the legs move independently.
 *
 * Requires BOTH dates to exist. A leg with no date is an absence rather than a disagreement, and there
 * is no date the reviewer could pick that equals null, so calling it a mismatch would only produce a
 * warning telling them to fix something they cannot.
 */
export function hasCoupledDeliveryDateMismatch(
  activeLeg: LegDetails,
  coupledSisterLeg: LegDetails | null,
): boolean {
  if (!coupledSisterLeg) return false

  const activeDate = getEffectiveDeliveryDate(activeLeg)
  const sisterDate = getEffectiveDeliveryDate(coupledSisterLeg)
  if (!activeDate || !sisterDate) return false

  return activeDate !== sisterDate
}

/** A leg is held while Fuze is holding it back from SAP. Only a held leg's date is ours to change. */
export function isLegHeld(leg: Pick<LegDetails, 'status'> | null | undefined): boolean {
  return leg?.status === 'HOLD'
}

/**
 * Whether the coupled sister leg pins the delivery date, and to what.
 *
 * The test is whether that leg is HELD — a leg that was released and a leg that was never flagged are
 * the same thing here. While both legs are held either date can move, because the edit is applied to
 * both. When the sister is not held its date is with SAP and cannot be changed, so the two legs can
 * only ever be brought into step by moving THIS leg onto the sister's date.
 *
 * Advisory only. The reviewer is told what the date should be and is left to set it; nothing is locked,
 * nothing is preselected, and release is never blocked. Note the sister's date may itself be in the
 * past, in which case the message names a date the picker will not accept — that reconciliation is
 * handled outside Fuze.
 */
export type CoupledDeliveryDateRule =
  | { kind: 'free' }
  | { kind: 'sisterNotHeld'; sisterDate: string }

export function getCoupledDeliveryDateRule(
  coupledSisterLeg: Pick<LegDetails, 'reqDelDate' | 'modRddDate' | 'status'> | null,
): CoupledDeliveryDateRule {
  if (!coupledSisterLeg || isLegHeld(coupledSisterLeg)) return { kind: 'free' }

  const sisterDate = getEffectiveDeliveryDate(coupledSisterLeg)
  // No date to name, so there is nothing useful to say.
  if (!sisterDate) return { kind: 'free' }

  return { kind: 'sisterNotHeld', sisterDate }
}

/** Delivery dates come back as `yyyy-MM-dd`; parse at local midnight so date-only compares are safe. */
export function parseDeliveryDate(date: string): Date {
  return new Date(date + 'T00:00:00')
}

/**
 * When the leg status is DELIVERED, determine the display label translation key.
 * Returns "orderStatus.delivered" when the DELIVERED journey step is complete (the
 * leg's proof-of-delivery timestamp is populated) or all boxes are at statusCode 200.
 * Otherwise returns "orderStatus.delivery" (still in the delivery process).
 */
export function getDeliveredDisplayLabel(leg: LegDetails): string {
  const deliveryStep = leg.journey.find((s) => s.step === 'DELIVERED')
  if (deliveryStep?.isComplete) {
    return 'orderStatus.delivered'
  }

  if (leg.boxes.length > 0) {
    const isAllDelivered = leg.boxes.every((box) => isDeliveredCode(box.statusCode))
    if (isAllDelivered) {
      return 'orderStatus.delivered'
    }
  }

  return 'orderStatus.delivery'
}

/**
 * Both legs' order audit trails, tagged with the leg they belong to and ordered oldest first.
 *
 * A coupled order is two sales orders, each with its own history in fz_sales_order_audit, and the
 * panel previously showed only the leg you happened to open — so the same order told a different
 * story depending on which leg you arrived from. Interleaved without a tag the entries would be
 * ambiguous, hence the label.
 *
 * Sorted here rather than relying on the query, which has no ORDER BY of its own.
 */
export function buildLegAuditTrails(response: CoupledOrder) {
  const legs = [
    { label: 'FMC', leg: response.legs.fmc },
    { label: 'NC', leg: response.legs.nc },
  ]

  return legs
    .flatMap(({ label, leg }) =>
      (leg?.auditTrails ?? []).map((trail) => ({ ...trail, legLabel: label })),
    )
    .sort((a, b) => (a.createdAt ?? '').localeCompare(b.createdAt ?? ''))
}

/**
 * The active leg flattened into the shape the leg-scoped cards render.
 *
 * Deliberately NOT annotated with a return type, and nothing here is cast. An assertion would let a
 * field dropped from this literal reach every consumer as `undefined` with the compiler silent,
 * which is the defect the derived contracts elsewhere exist to prevent. `LegOrder` below is
 * whatever this returns, so the two cannot disagree.
 */
export function buildLegOrder(response: CoupledOrder, leg: LegDetails) {
  return {
    legType: leg === response.legs.fmc ? ('FMC' as const) : ('NC' as const),
    poNumber: leg.poNumber,
    dynamoId: leg.dynamoId,
    status: leg.status,
    flagged: leg.flagged ?? false,
    carrier: response.carrier,
    customer: response.customer,
    offRoute: leg.offRoute,
    performance: response.performance,
    previousOrderDate: response.previousOrderDate ? new Date(response.previousOrderDate) : null,
    items: leg.items,
    boxes: leg.boxes,
    // Both legs, not just the active one: a coupled order is viewed as one order.
    orderAuditTrails: buildLegAuditTrails(response),
    journey: leg.journey,
    podStatus: leg.podStatus ?? null,
    reqDelDate: leg.reqDelDate ?? null,
    paymentTerms: leg.paymentTerms ?? null,
    flaggedForPaymentTerms: leg.flaggedForPaymentTerms ?? null,
    modPaymentTerm: leg.modPaymentTerm ?? null,
    ptrStatus: leg.ptrStatus ?? null,
    injInitiative: leg.injInitiative ?? null,
    injInitiativeName: leg.injInitiativeName ?? null,
  }
}

/** What the leg-scoped cards receive, derived from the builder rather than declared beside it. */
export type LegOrder = ReturnType<typeof buildLegOrder>

/** Build a LegJourney from the coupled response's per-leg journey arrays */
export function buildLegJourney(order: CoupledOrder): LegJourney {
  const fmcLeg = order.legs.fmc
  const ncLeg = order.legs.nc

  function findStep(journey: OrderJourneyStep[], step: NovaJourneyStep) {
    return journey.find((s) => s.step === step) ?? null
  }

  const legJourney: LegJourney = {}

  for (const step of NOVA_JOURNEY_STEPS) {
    const entries: LegJourney[string] = []

    if (fmcLeg) {
      const s = findStep(fmcLeg.journey, step)
      entries.push({
        legType: 'FMC',
        legNumber: fmcLeg.poNumber ?? fmcLeg.dynamoId,
        isComplete: s?.isComplete ?? step === 'RECEIVED',
        podStatus: fmcLeg.podStatus ?? null,
        documentNumber: s?.documentNumber,
        eventDate:
          s?.timestamp ??
          (step === 'RECEIVED' ? findStep(fmcLeg.journey, 'SUBMITTED')?.timestamp : undefined),
      })
    }

    if (ncLeg) {
      const s = findStep(ncLeg.journey, step)
      entries.push({
        legType: 'NC',
        legNumber: ncLeg.poNumber ?? ncLeg.dynamoId,
        isComplete: s?.isComplete ?? step === 'RECEIVED',
        podStatus: ncLeg.podStatus ?? null,
        documentNumber: s?.documentNumber,
        eventDate:
          s?.timestamp ??
          (step === 'RECEIVED' ? findStep(ncLeg.journey, 'SUBMITTED')?.timestamp : undefined),
      })
    }

    legJourney[step] = entries
  }

  return legJourney
}

function getCommentDynamoIds(comment: Comment): string[] {
  return 'dynamoIds' in comment ? (comment.dynamoIds ?? []) : []
}

/** Enrich comments with shared-across-legs indicator */
export function enrichCommentsWithSharing(comments: Comment[], hasBothLegs: boolean) {
  return comments.map((comment) => ({
    ...comment,
    isSharedAcrossLegs: hasBothLegs && getCommentDynamoIds(comment).length > 1,
  }))
}

/**
 * `fz_order_to_cash.shpord_decoupled_reason` carries values written by Nova, not by us, and they are
 * a mix of screaming-snake codes and prose. Values seen in the database (2026-08-20), plus the
 * sentinel the backend adds for an account whose fulfillment method is Separate Orders:
 *
 *   Business Reason: Manual release via API   Cutoff reached for DC CA41 / CA43 / CA48
 *   EDI_CUTOFF_SEND_IMMEDIATELY               GLOBAL_DECOUPLING_ON
 *   LOV4_SEPARATE_SHIPMENT                    POCONF_CHANGED_DELIVERY_DATES
 *   FULFILLMENT_METHOD_SEPARATE_ORDERS
 *
 * The codes are sentence-cased for display; everything else is already readable and passes through
 * untouched. Doing it mechanically rather than by lookup means a code Nova adds later still reads as
 * a sentence instead of shouting, with nothing to maintain. These are upstream data values, so they
 * are not translated — same as before, when the raw value was shown.
 */
const SCREAMING_SNAKE = /^[A-Z0-9]+(?:_[A-Z0-9]+)+$/

/**
 * Tokens that stay upper-case through the sentence-casing, because lower-casing them turns a known
 * acronym into a word that reads as a typo ("Edi", "Poconf"). Add to this only for real acronyms.
 */
const PRESERVED_ACRONYMS = new Set([
  'EDI',
  'DC',
  'LOV4',
  'POCONF',
  'API',
  'FMC',
  'NC',
  'O2C',
  'SAP',
])

export function formatDecoupledReason(reason: string | null): string | null {
  if (!reason) return null
  if (!SCREAMING_SNAKE.test(reason)) return reason

  const words = reason
    .split('_')
    .map((word) => (PRESERVED_ACRONYMS.has(word) ? word : word.toLowerCase()))

  // Capitalise the opening word unless it is an acronym we just preserved.
  const [first, ...rest] = words
  const opening = PRESERVED_ACRONYMS.has(first!)
    ? first!
    : first!.charAt(0).toUpperCase() + first!.slice(1)

  return [opening, ...rest].join(' ')
}

/**
 * The Nova order-details shape, or a thrown error saying why it is not.
 *
 * The counterpart to `mainlineOrderDetails`: `GET /orders/:dynamoId` is one route with two
 * implementations, so its inferred type is a union and the Nova tree needs the coupled half.
 * It checks the environment as well as the payload, because the environment is what states which
 * variant should have answered; narrowing on `legs` alone would quietly render a mainline response
 * as an empty Nova one.
 */
export function novaOrderDetails(result: object): CoupledOrder {
  if (!isNova) {
    throw new Error('Order details: the Nova tree was rendered on a mainline environment.')
  }

  if (!('legs' in result)) {
    throw new Error('Order details: expected a coupled Nova response and received a flat one.')
  }

  return result as CoupledOrder
}
